/**
 * Constants for Prompt Settings Enhancement
 * 
 * Contains tooltip content, validation rules, and available models
 * for the Bedrock prompt management interface.
 */

// Tooltip content for each prompt setting
// _Requirements: 3.3, 4.3, 5.3, 6.4, 7.2, 8.2_
export const SETTING_TOOLTIPS = {
  name: "A unique identifier for this prompt. Used to reference the prompt in API calls and the console.",
  
  description: "A human-readable description of what this prompt does. Helps team members understand the prompt's purpose.",
  
  temperature: "Controls randomness in responses. Lower values (closer to 0) make output more deterministic and focused. Higher values (closer to 1) make output more creative and varied. Default: 0.7",
  
  topP: "Controls diversity via nucleus sampling. The model considers tokens with top_p probability mass. Lower values make output more focused, higher values allow more variety. Use either temperature OR top_p, not both. Default: 0.9",
  
  maxTokens: "Maximum number of tokens to generate in the response. Higher values allow longer responses but increase cost and latency. The actual maximum depends on the selected model. Default: 2000",
  
  stopSequences: "Sequences that signal the model to stop generating. When the model generates any of these sequences, it will stop. Useful for controlling output format. Example: ['\\n\\n', 'END']",
  
  modelId: "The foundation model to use for this prompt. Different models have different capabilities, costs, and token limits. Claude 3 Sonnet balances performance and cost. Haiku is faster and cheaper. Opus is most capable.",
  
  templateType: "TEXT: Single text prompt template. CHAT: Conversational format with system/user/assistant messages. Use TEXT for simple prompts, CHAT for multi-turn conversations.",
  
  template: "The prompt template text that will be sent to the model. Use {{variable}} syntax for dynamic content that will be replaced at runtime."
};

// Validation rules for prompt settings
// _Requirements: 3.2, 4.2, 5.2_
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 1,
    maxLength: 100,
    errorMessage: 'Prompt name is required (1-100 characters)'
  },
  description: {
    required: false,
    maxLength: 500,
    errorMessage: 'Description must be 500 characters or less'
  },
  temperature: {
    min: 0.0,
    max: 1.0,
    type: 'number',
    errorMessage: 'Temperature must be between 0 and 1'
  },
  topP: {
    min: 0.0,
    max: 1.0,
    type: 'number',
    errorMessage: 'Top P must be between 0 and 1'
  },
  maxTokens: {
    min: 1,
    max: 200000,
    type: 'integer',
    errorMessage: 'Max tokens must be a positive integer'
  },
  stopSequences: {
    maxItems: 4,
    maxItemLength: 1000,
    errorMessage: 'Maximum 4 stop sequences, each up to 1000 characters'
  },
  template: {
    required: true,
    minLength: 1,
    errorMessage: 'Prompt template is required'
  }
};

// Available Bedrock models
// _Requirements: 7.1_
export const AVAILABLE_MODELS = [
  { 
    id: 'anthropic.claude-3-sonnet-20240229-v1:0', 
    name: 'Claude 3 Sonnet', 
    maxTokens: 200000,
    description: 'Balanced performance and cost'
  },
  { 
    id: 'anthropic.claude-3-haiku-20240307-v1:0', 
    name: 'Claude 3 Haiku', 
    maxTokens: 200000,
    description: 'Fastest and most affordable'
  },
  { 
    id: 'anthropic.claude-3-opus-20240229-v1:0', 
    name: 'Claude 3 Opus', 
    maxTokens: 200000,
    description: 'Most capable and intelligent'
  },
  { 
    id: 'anthropic.claude-3-5-sonnet-20240620-v1:0', 
    name: 'Claude 3.5 Sonnet', 
    maxTokens: 200000,
    description: 'Latest Sonnet with improved capabilities'
  }
];

// Template types
export const TEMPLATE_TYPES = [
  { id: 'TEXT', name: 'Text', description: 'Single text prompt template' },
  { id: 'CHAT', name: 'Chat', description: 'Conversational format with messages' }
];

// Default form values (aligned with backend Lambda defaults)
export const DEFAULT_FORM_VALUES = {
  name: '',
  description: '',
  templateType: 'TEXT',
  templateConfiguration: {
    text: { text: '' }
  },
  modelId: 'anthropic.claude-3-sonnet-20240229-v1:0',
  inferenceConfiguration: {
    text: {
      temperature: 0.7,
      topP: 0.9,
      maxTokens: 2000,
      stopSequences: []
    }
  }
};
